/**
 * Clears all user-scoped data from IndexedDB and localStorage on logout.
 *
 * This prevents data leakage when switching between accounts on the same
 * browser. The server is the source of truth — everything cleared here
 * is a local performance cache that will be rebuilt on next login.
 */

import { deactivateEntityStore, getDbName } from './entity-store';
import { resetClientIdentity } from './client-identity';
import { useWorkspaceStore } from '@/stores/use-workspace-store';

const NULA_IDB_PREFIX = 'nula-';

/**
 * Delete every IndexedDB database whose name starts with "nula-".
 * Covers: nula-entity-store, nula-sync-{entityId}, and any future nula-* dbs.
 */
async function deleteAllNulaIndexedDBs(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;

  // indexedDB.databases() is supported in Chrome, Edge, Firefox 126+, Safari 17+.
  if (typeof indexedDB.databases === 'function') {
    try {
      const dbs = await indexedDB.databases();
      const deletions = dbs
        .filter((db) => db.name?.startsWith(NULA_IDB_PREFIX))
        .map(
          (db) =>
            new Promise<void>((resolve) => {
              const req = indexedDB.deleteDatabase(db.name!);
              req.onsuccess = () => resolve();
              req.onerror = () => {
                console.warn(`[clearUserData] failed to delete IDB: ${db.name}`);
                resolve(); // best-effort — don't block logout
              };
              req.onblocked = () => {
                console.warn(`[clearUserData] IDB delete blocked (open connections): ${db.name}`);
                resolve(); // don't block logout — DB will be deleted when connections close
              };
            }),
        );
      await Promise.all(deletions);
    } catch {
      console.warn('[clearUserData] indexedDB.databases() failed, falling back to known names');
      await deleteKnownDatabases();
    }
  } else {
    await deleteKnownDatabases();
  }
}

/**
 * Fallback for browsers that don't support indexedDB.databases().
 * Deletes the per-workspace IDB for each known workspace, plus the legacy
 * singleton IDB from pre-M10b clients.
 */
async function deleteKnownDatabases(): Promise<void> {
  // Per-workspace IDBs: iterate workspaces from the M9 store.
  const workspaces = useWorkspaceStore.getState().workspaces;
  const workspaceNames = workspaces.map((w) => getDbName(w.id));

  // Also include the legacy singleton IDB for users upgrading from pre-M10b clients.
  const legacyName = 'nula-entity-store';
  const allNames = [...workspaceNames, legacyName];

  const deletions = allNames.map(
    (name) =>
      new Promise<void>((resolve) => {
        const req = indexedDB.deleteDatabase(name);
        req.onsuccess = () => resolve();
        req.onerror = () => resolve();
        req.onblocked = () => resolve();
      }),
  );
  await Promise.all(deletions);
  // nula-sync-{entityId} databases can't be enumerated in the fallback path.
  // They'll be orphaned but won't leak data because the entity store (which
  // maps docIds to users) is deleted, so the new user will never open them.
}

/**
 * Clear nula-specific localStorage and sessionStorage keys.
 */
function clearLocalStorageKeys(): void {
  // Workspace store — reset in-memory state and remove the persist
  // key so the next login doesn't briefly show stale workspace data before
  // refreshFromServer fires.
  useWorkspaceStore.setState({ workspaces: [], activeWorkspaceId: null });
  localStorage.removeItem('nula-workspace');

  // Client group ID — per-device identity that must not carry across users
  localStorage.removeItem('nula-client-group-id');

  // LRU tracker keys (nula-lru-{userId})
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('nula-lru-')) keysToRemove.push(key);
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  // Per-tab client ID
  sessionStorage.removeItem('nula-client-id');
}

/**
 * Reset in-memory caches that hold user data.
 */
function resetInMemoryState(): void {
  // Deactivate the entity store: sets _activeStore to null so post-logout
  // accesses throw rather than reading stale data. The IDB itself is cleared
  // by deleteAllNulaIndexedDBs() which runs fire-and-forget below.
  deactivateEntityStore();
  // Invalidate cached client identity so fresh UUIDs are generated on next login
  resetClientIdentity();
}

/**
 * Clear all user-scoped data. Call this on logout, before clearing auth state.
 *
 * In-memory and localStorage cleanup is synchronous — takes effect immediately.
 * IndexedDB deletion is fire-and-forget: deleteDatabase may be blocked by open
 * y-indexeddb connections that only close when the SyncSystem unmounts (which
 * happens *after* the auth state change triggers a re-render). Awaiting the
 * deletion would deadlock on desktop where there's no page navigation.
 */
export function clearUserData(): void {
  resetInMemoryState();
  clearLocalStorageKeys();
  // Fire-and-forget — don't block logout on IDB deletion
  deleteAllNulaIndexedDBs().catch((err) => {
    console.warn('[clearUserData] IDB cleanup failed:', err);
  });
}
